import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel, Button, Icon } from '../common'
import './ImportProgress.css'

/**
 * ImportProgress - Batch import progress overlay
 */
export default function ImportProgress({ 
  isOpen, 
  progress, 
  onClose 
}) {
  const { 
    total = 0, 
    completed = 0, 
    current = '', 
    status = 'importing',
    results = [] 
  } = progress || {}

  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0
  const isComplete = status === 'complete' || status === 'error'

  const successCount = results.filter(r => r.success).length
  const errorCount = results.filter(r => !r.success).length

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="progress-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="progress-modal"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
          >
            <GlassPanel glow={isComplete ? (errorCount > 0 ? 'amber' : 'cyan') : undefined} padding="lg">
              <div className="progress-header">
                <h2 className="progress-title">
                  {isComplete ? 'Import Complete' : 'Importing...'}
                </h2>
                {!isComplete && (
                  <p className="progress-current">
                    {current || 'Preparing...'}
                  </p>
                )}
              </div>

              {/* Progress bar */}
              <div className="progress-bar-container">
                <motion.div 
                  className="progress-bar"
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              
              <div className="progress-stats">
                <span className="progress-count">
                  {completed} / {total}
                </span>
                <span className="progress-percentage">
                  {percentage}%
                </span>
              </div>

              {/* Results summary when complete */}
              {isComplete && (
                <div className="progress-results">
                  <div className="progress-result progress-result--success">
                    <span className="progress-result-icon"><Icon name="check" size={16} /></span>
                    <span>{successCount} imported</span>
                  </div>
                  {errorCount > 0 && (
                    <div className="progress-result progress-result--error">
                      <span className="progress-result-icon"><Icon name="x" size={16} /></span>
                      <span>{errorCount} failed</span>
                    </div>
                  )}
                </div>
              )}

              {/* Error details */}
              {isComplete && errorCount > 0 && (
                <div className="progress-errors">
                  <p className="progress-errors-title">Failed items:</p>
                  <ul className="progress-errors-list">
                    {results
                      .filter(r => !r.success)
                      .map((r, i) => (
                        <li key={i}>
                          <span className="error-filename">{r.filename}</span>
                          <span className="error-message">{r.error}</span>
                        </li>
                      ))
                    }
                  </ul>
                </div>
              )}

              {/* Actions */}
              {isComplete && (
                <div className="progress-actions">
                  <Button variant="primary" onClick={onClose}>
                    Done
                  </Button>
                </div>
              )}

              {/* Animated spinner when not complete */}
              {!isComplete && (
                <div className="progress-spinner">
                  <motion.div
                    className="spinner"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                </div>
              )}
            </GlassPanel>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

